import { useState } from "react";
import { motion } from "framer-motion";
import { Expand, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";
import { CERTIFICATES } from "@/lib/content";

export default function Certificates() {
  const { t, lang } = useSite();
  const [active, setActive] = useState(null);

  return (
    <section id="certificates" className="relative py-24 md:py-36 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-ibk" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-ibk">{t.certs.label}</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.05] max-w-2xl">
            {t.certs.title}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl">{t.certs.sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {CERTIFICATES.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08}>
              <motion.button
                whileHover={{ y: -6 }}
                onClick={() => setActive(c)}
                data-testid={`cert-thumb-${i}`}
                className="group relative w-full overflow-hidden rounded-md border border-border bg-[hsl(var(--card))] text-left transition-colors duration-400 hover:border-ibk"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[hsl(var(--muted))]">
                  <img
                    src={c.img}
                    alt={lang === "de" ? c.de : c.en}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-400" />
                  <span className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/90 text-neutral-900 opacity-0 translate-y-1 transition-all duration-400 group-hover:opacity-100 group-hover:translate-y-0">
                    <Expand size={16} />
                  </span>
                </div>
                <div className="p-4">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-ibk font-bold">{c.issuer}</span>
                  <p className="mt-1 text-sm text-foreground leading-snug line-clamp-2">{lang === "de" ? c.de : c.en}</p>
                </div>
              </motion.button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="cert-lightbox"
          className="max-w-3xl w-[95vw] p-0 border-border bg-[hsl(var(--card))] overflow-hidden [&>button]:hidden"
        >
          {active && (
            <div className="flex flex-col max-h-[92vh]">
              <DialogTitle className="sr-only">{lang === "de" ? active.de : active.en}</DialogTitle>
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <span className="text-sm font-semibold text-foreground pr-4">{lang === "de" ? active.de : active.en}</span>
                <button
                  onClick={() => setActive(null)}
                  data-testid="cert-lightbox-close"
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-ibk transition-colors"
                >
                  {t.certs.close} <X size={15} />
                </button>
              </div>
              <div className="overflow-auto p-4 md:p-6 bg-[hsl(var(--muted))]/40">
                <img src={active.img} alt={lang === "de" ? active.de : active.en} className="mx-auto max-h-[75vh] w-auto rounded-sm shadow-lg" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
